/**
 * Pure model-building for the cash-flow sankey. Everything here is derived
 * from data — category names and the fold into "everything else" come from
 * the rollup rows, never from hardcoded names, and no annotation is needed
 * anywhere: the diagram balances by explicit remainder bands ("Saving" when
 * income exceeded outflows, "From savings" when outflows exceeded income).
 *
 * The overview reads left to right: income sources merge into a single
 * income spine, the spine splits three ways (taxes / spending / saving),
 * and Spending fans out into categories. Merging at the spine dissolves
 * the "which dollars funded what" question — deposited money is fungible,
 * so no per-source attribution is claimed at all. The display order still
 * tells the household story by adjacency: the paycheck sits at the top
 * beside the Taxes and Spending offtakes, other incomes rise by size, the
 * largest windfall lands beside Saving, and a bound retirement category
 * pins to the very bottom — its dollars are definitionally savings.
 *
 * Amounts are integer cents throughout; formatting happens at render time.
 */

export type FlowGroup = {
  /** Category id / payee; null collects rows missing the field entirely. */
  key: string | null;
  net: number;
};

export type FlowCategoryInfo = {
  _id: string;
  name: string;
};

/** A category group (Settings overlay) joined in for the group tier. */
export type FlowCategoryGroupInfo = {
  _id: string;
  name: string;
  categoryIds: string[];
  color: string | null;
};

export type FlowNodeKind =
  // An income source (left side).
  | 'income'
  // The left-side remainder band when outflows exceeded the window's income.
  | 'reserve-draw'
  // A spend destination (right side); clickable when it carries a key.
  | 'spend'
  // A multi-category group aggregate in the group column; its member
  // categories fan out to its right. Not drillable (children are visible).
  | 'spend-group'
  // The right-side remainder band on the payee drill.
  | 'saved'
  // Take-home bands on the payee drill (money to/from the rest of the map).
  | 'take-home'
  // Gross pay withheld for taxes before it ever reached an account — shown
  // as its own source band only when no paycheck category is bound.
  | 'withheld'
  // Raw rows nothing has consumed yet, netted: a to-do band, not a
  // category. Sits on the source side when the net is an inflow and at
  // the foot of the allocation column when it is an outflow. Never
  // drillable.
  | 'unreviewed'
  // The middle-tier allocation nodes.
  | 'taxes-tier'
  | 'spending-tier'
  | 'saving-tier';

export type FlowNode = {
  /** Grouping key (category id / payee) when this node is one group. */
  key: string | null;
  label: string;
  /**
   * Category-group linkage: on a 'spend-group' node, the group's own id;
   * on a category in the cats column, its parent group's id (the renderer
   * fans each parent to exactly its children).
   */
  groupId?: string;
  /** User-chosen color name (category color list) on 'spend-group' nodes. */
  color?: string | null;
  /** Additional context rendered under the label (e.g. folded count). */
  sublabel?: string;
  /** Always positive; the node's share of its column. */
  value: number;
  /**
   * Height the node draws at when a scale break compresses it; labels keep
   * showing `value`. Absent means drawn at true scale.
   */
  displayValue?: number;
  kind: FlowNodeKind;
};

export type TieredTotals = {
  income: number;
  taxes: number;
  spending: number;
  saving: number;
  fromSavings: number;
  /**
   * Signed net of the window's unreviewed raw rows (negative = outflow) —
   * drawn as its own band and kept out of every other figure.
   */
  unreviewed: number;
  unreviewedCount: number;
};

/**
 * The spine needs no link list: sources stack into it in order, it splits
 * into the allocations in order, and Spending fans into the categories in
 * order — all geometry the renderer derives from the three node columns.
 */
export type TieredFlowModel = {
  sources: FlowNode[];
  allocs: FlowNode[];
  /**
   * The group tier: multi-category groups as aggregates, plus standalone
   * categories (ungrouped, or sole active member of their group) which end
   * here. With no groups configured this is exactly the category fold and
   * `cats` is empty — the diagram renders four columns as before.
   */
  groupCol: FlowNode[];
  /** Member categories of the multi-category groups, in group order. */
  cats: FlowNode[];
  totals: TieredTotals;
  /**
   * Set when the dominant pass-through pair is drawn compressed — the
   * caption explains the break and a toggle restores true scale.
   */
  scaleBreak: {label: string; hidden: number} | null;
};

// The payee drill keeps the original two-column shape.
export type FlowTotals = {
  spend: number;
  income: number;
  covered: number;
  fromSavings: number;
  saved: number;
};

export type FlowModel = {
  sources: FlowNode[];
  dests: FlowNode[];
  totals: FlowTotals;
  scaleBreak: {label: string; hidden: number} | null;
};

/** Sides always balance; the caller can rely on it for layout. */
const totalOf = (nodes: FlowNode[]): number =>
  nodes.reduce((sum, node) => sum + node.value, 0);

/**
 * A node's share of its column, as label text — whole percents, "<1%" for
 * slivers, null when the base is empty or the share is the whole column
 * (a lone node's "100%" is noise, not information).
 */
export const formatShare = (value: number, base: number): string | null => {
  // "<1%" is for positive slivers only — an exact zero shows no share at
  // all rather than implying one.
  if (base <= 0 || value <= 0 || value >= base) {
    return null;
  }
  const share = value / base;
  return share < 0.005 ? '<1%' : `${Math.round(share * 100)}%`;
};

/**
 * Keep the largest `keep` rows and fold the tail into one node, so the
 * diagram caps its node count no matter how many categories exist. A tail
 * of one is not folded — a fold node that replaces a single row would hide
 * information without saving space.
 */
const foldTail = (
  nodes: FlowNode[],
  keep: number,
  foldLabel: string,
): FlowNode[] => {
  if (nodes.length <= keep + 1) {
    return nodes;
  }
  const kept = nodes.slice(0, keep);
  const tail = nodes.slice(keep);
  return [
    ...kept,
    {
      key: null,
      label: foldLabel,
      sublabel: `${tail.length} smaller`,
      value: totalOf(tail),
      kind: tail[0].kind,
    },
  ];
};

const byValueDesc = (a: FlowNode, b: FlowNode): number => b.value - a.value;

/**
 * Member categories below this share of Spending fold into their group's
 * "Other" node. Count caps never fire on a well-grouped long tail (no
 * single group exceeds them), yet most drawn nodes explain almost none of
 * the money — the floor folds by contribution instead. "Show all
 * categories" disables it along with the count caps.
 */
const MEMBER_SHARE_FLOOR = 0.02;

export type FlowPayroll = {
  /** Taxes withheld across the window's paychecks, in cents. */
  taxesWithheld: number;
  /**
   * The category bound to the `taxesCategory` slot, if any — its cash net
   * (estimated payments out, refunds in) merges with the withheld amount
   * into the Taxes tier. Null labels the tier "Taxes withheld".
   */
  taxesCategoryId: string | null;
  /**
   * The category bound to the `paycheckCategory` slot, if any — the
   * withheld amount merges into its income node, so the node shows
   * deposits plus the taxes withheld from them. (Deliberately not full
   * gross: 401(k)/HSA are savings, and other pre-tax costs have no band
   * yet.) Null keeps the standalone "Withheld pay" band.
   */
  paycheckCategoryId: string | null;
  /**
   * The category bound to the `retirementCategory` slot, if any — its
   * inflows (401(k)/HSA contributions arriving in their own accounts) are
   * income that is definitionally savings, so the node pins to the bottom
   * of the source column beside the Saving tier, which notes it.
   */
  retirementCategoryId: string | null;
};

export type FlowUnreviewed = {
  /** Signed net of the unconsumed USD rows, in cents. */
  net: number;
  count: number;
};

type BuildTieredFlowArgs = {
  groups: FlowGroup[];
  categoriesById: Map<string, FlowCategoryInfo>;
  /**
   * The window's raw rows that no transaction has consumed yet. The rollups
   * exclude them, so without this band their outflows would silently read
   * as Saving. They have no category (and may be transfer legs, which
   * netting mostly cancels), so they draw as one to-do band, never as
   * spending.
   */
  unreviewed?: FlowUnreviewed;
  /** Category groups (Settings overlay) for the group tier; may be empty. */
  categoryGroups?: FlowCategoryGroupInfo[];
  /**
   * Imported payroll for the window. Absent or zero-withheld payroll leaves
   * the map exactly as the ledger tells it (deposits are net pay).
   */
  payroll?: FlowPayroll;
  /** Most rows to show per side before folding the tail. */
  maxNodes?: number;
};

/**
 * A windfall that arrives and sits unspent dominates the map as a
 * pass-through pair: the largest income node and the Saving tier are mostly
 * the same dollars, and drawn at true scale they crush every real flow into
 * slivers. When the pair dwarfs the largest other node, both members — and
 * the ribbon joining them — shrink by the same amount, so everything else
 * stays proportionate while labels keep true values.
 */
const SCALE_BREAK_FACTOR = 2;

const applyTieredScaleBreak = (model: TieredFlowModel): TieredFlowModel => {
  const saving = model.allocs.find((node) => node.kind === 'saving-tier');
  const incomes = model.sources.filter((node) => node.kind === 'income');
  if (saving == null || incomes.length === 0) {
    return model;
  }
  const largestIncome = incomes.reduce((a, b) => (b.value > a.value ? b : a));
  const reference = Math.max(
    0,
    ...[...model.sources, ...model.allocs, ...model.groupCol, ...model.cats]
      .filter((node) => node !== saving && node !== largestIncome)
      .map((node) => node.value),
  );
  const cap = SCALE_BREAK_FACTOR * reference;
  const passThrough = Math.min(saving.value, largestIncome.value);
  if (reference <= 0 || passThrough <= cap) {
    return model;
  }
  const hidden = passThrough - cap;
  const compressNode = (node: FlowNode): FlowNode => ({
    ...node,
    displayValue: node.value - hidden,
  });
  return {
    ...model,
    sources: model.sources.map((node) =>
      node === largestIncome ? compressNode(node) : node,
    ),
    allocs: model.allocs.map((node) =>
      node === saving ? compressNode(node) : node,
    ),
    scaleBreak: {label: largestIncome.label, hidden},
  };
};

/** The same model drawn at true proportions (labels are already true). */
export const atTrueScale = (model: TieredFlowModel): TieredFlowModel => ({
  ...model,
  sources: model.sources.map(({displayValue: _d, ...node}) => node),
  allocs: model.allocs.map(({displayValue: _d, ...node}) => node),
  groupCol: model.groupCol.map(({displayValue: _d, ...node}) => node),
  cats: model.cats.map(({displayValue: _d, ...node}) => node),
});

/**
 * The tiered overview. Construction order matters: payroll merges first
 * (they change which groups remain), then the income and category columns,
 * then the allocation tier from their totals, then ribbons.
 */
export const buildTieredFlow = ({
  groups,
  categoriesById,
  categoryGroups = [],
  payroll,
  unreviewed,
  maxNodes = 7,
}: BuildTieredFlowArgs): TieredFlowModel => {
  // Null keys are the server's bucket for payments whose category was
  // unset; they stay in the totals under a plain name and aren't drillable.
  const labelFor = (key: string | null): string =>
    key == null
      ? 'Uncategorized'
      : (categoriesById.get(key)?.name ?? 'Unknown category');

  const withheld =
    payroll != null && payroll.taxesWithheld > 0 ? payroll.taxesWithheld : 0;
  let working = groups;

  // Paycheck merge: the withheld dollars join the paycheck's own income
  // node (deposits plus withholding — see FlowPayroll for why that is not
  // full gross); without a binding they render as the standalone band.
  let paycheckNode: FlowNode | null = null;
  if (withheld > 0) {
    const paycheckKey = payroll?.paycheckCategoryId ?? null;
    const paycheckGroup =
      paycheckKey != null
        ? (working.find((group) => group.key === paycheckKey) ?? null)
        : null;
    // Only a positive-net paycheck group merges: a net-negative window
    // (reversal-heavy) would make the node smaller than the exact
    // withholding link it must emit, breaking ribbon conservation — the
    // category stays on the spend side and the standalone band carries
    // the withholding instead.
    if (paycheckGroup != null && paycheckGroup.net > 0) {
      working = working.filter((group) => group !== paycheckGroup);
      paycheckNode = {
        key: paycheckKey,
        label: labelFor(paycheckKey),
        sublabel: 'incl. withheld pay',
        value: paycheckGroup.net + withheld,
        kind: 'income',
      };
    }
  }

  // Retirement pin: the bound category's inflow is income that is
  // definitionally savings, so it sits at the very bottom of the source
  // column, beside the Saving tier it becomes. Display-only — its dollars
  // flow through the spine like any other income.
  let retirementNode: FlowNode | null = null;
  const retirementKey = payroll?.retirementCategoryId ?? null;
  const retirementGroup =
    retirementKey != null
      ? (working.find((group) => group.key === retirementKey) ?? null)
      : null;
  if (retirementGroup != null && retirementGroup.net > 0) {
    working = working.filter((group) => group !== retirementGroup);
    retirementNode = {
      key: retirementKey,
      label: labelFor(retirementKey),
      value: retirementGroup.net,
      kind: 'income',
    };
  }

  // Taxes tier: withheld plus the bound category's cash net (payments add,
  // refunds subtract). A refund surplus flows back to the income side under
  // the category's own name.
  const taxesKey = payroll?.taxesCategoryId ?? null;
  const taxesGroup =
    taxesKey != null
      ? (working.find((group) => group.key === taxesKey) ?? null)
      : null;
  if (taxesGroup != null) {
    working = working.filter((group) => group !== taxesGroup);
  }
  const taxesTier = Math.max(0, withheld - (taxesGroup?.net ?? 0));
  const taxesRefund = Math.max(0, (taxesGroup?.net ?? 0) - withheld);

  const income = working.filter((group) => group.net > 0);
  const spend = working.filter((group) => group.net < 0);

  // The paycheck node is the withholding carrier: the ribbon attribution
  // needs it as a distinct node, so it never folds into "Other income" —
  // only the other incomes compete for the remaining slots.
  const foldableIncome: FlowNode[] = [
    ...income.map(
      (group): FlowNode => ({
        key: group.key,
        label: labelFor(group.key),
        value: group.net,
        kind: 'income',
      }),
    ),
    ...(taxesRefund > 0 && taxesGroup != null
      ? [
          {
            key: taxesKey,
            label: labelFor(taxesKey),
            value: taxesRefund,
            kind: 'income' as const,
          },
        ]
      : []),
  ].sort(byValueDesc);
  // Display order tells the story by adjacency through the spine: the
  // withholding carrier leads (beside the Taxes and Spending offtakes),
  // other incomes rise in value so the largest lands beside Saving, and
  // the pinned retirement node sits at the very bottom.
  const reserved =
    (paycheckNode != null ? 1 : 0) + (retirementNode != null ? 1 : 0);
  // The same share floor as the member fans: income slivers fold into
  // "Other income" by contribution, with the count cap as backstop. The
  // floor is measured against the whole income side (carrier and pins
  // included), not just the foldable remainder.
  const incomeBase =
    totalOf(foldableIncome) +
    (paycheckNode?.value ?? 0) +
    (retirementNode?.value ?? 0) +
    (paycheckNode == null ? withheld : 0);
  const incomeFloor = Number.isFinite(maxNodes)
    ? MEMBER_SHARE_FLOOR * incomeBase
    : 0;
  const keptIncome = foldableIncome.filter(
    (node) => node.value >= incomeFloor,
  );
  const incomeTail = foldableIncome.filter(
    (node) => node.value < incomeFloor,
  );
  const flooredIncome =
    incomeTail.length <= 1
      ? foldableIncome
      : [
          ...keptIncome,
          {
            key: null,
            label: 'Other income',
            sublabel: `${incomeTail.length} smaller`,
            value: totalOf(incomeTail),
            kind: 'income' as const,
          },
        ];
  const folded = foldTail(
    flooredIncome,
    Math.max(1, maxNodes - reserved),
    'Other income',
  ).sort((a, b) => a.value - b.value);
  const sources: FlowNode[] = [
    ...(paycheckNode != null ? [paycheckNode] : []),
    ...(withheld > 0 && paycheckNode == null
      ? [
          {
            key: null,
            label: 'Withheld pay',
            sublabel: 'taxes taken before deposit',
            value: withheld,
            kind: 'withheld' as const,
          },
        ]
      : []),
    ...folded,
    ...(retirementNode != null ? [retirementNode] : []),
  ];

  // Group tier: partition the spend categories by their category group.
  // A group with two or more active members becomes an aggregate that fans
  // into them; a group with one active member, and every ungrouped
  // category, stands as itself and ends in the group column — a child that
  // duplicates its parent would add space without information.
  const spendNodes: FlowNode[] = spend
    .map(
      (group): FlowNode => ({
        key: group.key,
        label: labelFor(group.key),
        value: -group.net,
        kind: 'spend',
      }),
    )
    .sort(byValueDesc);

  const groupByCategory = new Map<string, FlowCategoryGroupInfo>();
  for (const categoryGroup of categoryGroups) {
    for (const id of categoryGroup.categoryIds) {
      groupByCategory.set(id, categoryGroup);
    }
  }
  const memberships = new Map<string, FlowNode[]>();
  const standalone: FlowNode[] = [];
  for (const node of spendNodes) {
    const categoryGroup =
      node.key != null ? groupByCategory.get(node.key) : undefined;
    if (categoryGroup == null) {
      standalone.push(node);
    } else {
      const members = memberships.get(categoryGroup._id) ?? [];
      members.push(node);
      memberships.set(categoryGroup._id, members);
    }
  }
  const groupEntries: FlowNode[] = [...standalone];
  for (const [groupId, members] of memberships) {
    const info = categoryGroups.find(
      (categoryGroup) => categoryGroup._id === groupId,
    );
    if (members.length === 1 || info == null) {
      groupEntries.push(...members);
    } else {
      // No "N categories" sublabel: the fan to the node's right already
      // shows the members, and the extra text collides with child labels
      // when the bar is compact.
      groupEntries.push({
        key: null,
        groupId,
        label: info.name,
        color: info.color,
        value: totalOf(members),
        kind: 'spend-group',
      });
    }
  }
  const groupCol = foldTail(
    groupEntries.sort(byValueDesc),
    maxNodes,
    'Everything else',
  );
  // Children fan out only for aggregates that survived the fold — a group
  // folded into the tail keeps its dollars inside the fold node.
  const spendingTotal = totalOf(groupCol);
  const memberFloor = Number.isFinite(maxNodes)
    ? MEMBER_SHARE_FLOOR * spendingTotal
    : 0;
  const cats: FlowNode[] = groupCol.flatMap((entry) => {
    if (entry.kind !== 'spend-group' || entry.groupId == null) {
      return [];
    }
    const members = memberships.get(entry.groupId) ?? [];
    const kept = members.filter((member) => member.value >= memberFloor);
    const tail = members.filter((member) => member.value < memberFloor);
    // No member clears the floor: the group node stands terminal — a fan
    // of nothing but "Other" would duplicate the parent.
    if (kept.length === 0) {
      return [];
    }
    // A tail of one keeps its name (a fold node replacing a single row
    // hides information without saving space).
    // Plain "Other": the ribbon already names the group, and the long
    // form truncated into noise while clipping the right edge.
    const shaped =
      tail.length <= 1
        ? members
        : [
            ...kept,
            {
              key: null,
              label: 'Other',
              sublabel: `${tail.length} smaller`,
              value: totalOf(tail),
              kind: 'spend' as const,
            },
          ];
    return foldTail(shaped, maxNodes, `Other ${entry.label}`).map(
      (member): FlowNode => ({...member, groupId: entry.groupId}),
    );
  });

  // totals.income is real income only — the balancing "From savings" band
  // joins the source column (so ribbons conserve) but never the caption's
  // income figure, which would overstate the window and double-describe
  // the draw. The unreviewed band is kept out the same way: its dollars
  // are nobody's income or spending until someone allocates them.
  const incomeTotal = totalOf(sources);
  const unreviewedNet = unreviewed?.net ?? 0;
  const unreviewedNode = (value: number): FlowNode => ({
    key: null,
    label: 'Unreviewed',
    // Short: the band is usually a sliver, drawn with a one-line label.
    // The caption carries the full explanation.
    sublabel: `${unreviewed?.count ?? 0} to review`,
    value,
    kind: 'unreviewed',
  });
  if (unreviewedNet > 0) {
    sources.push(unreviewedNode(unreviewedNet));
  }
  const unreviewedOut = Math.max(0, -unreviewedNet);
  let saving =
    incomeTotal +
    Math.max(0, unreviewedNet) -
    taxesTier -
    spendingTotal -
    unreviewedOut;
  let fromSavings = 0;
  if (saving < 0) {
    fromSavings = -saving;
    saving = 0;
    sources.push({
      key: null,
      label: 'From savings',
      sublabel: 'outflows beyond the window’s income',
      value: fromSavings,
      kind: 'reserve-draw',
    });
  }

  const allocs: FlowNode[] = [];
  if (taxesTier > 0) {
    allocs.push({
      // Drillable into the bound category's payees when a binding exists;
      // the binding also names the tier — a window with withholding but no
      // cash tax rows still belongs to the bound category.
      key: taxesKey,
      label: taxesKey != null ? labelFor(taxesKey) : 'Taxes withheld',
      sublabel:
        taxesKey != null && withheld > 0
          ? 'incl. payroll withholding'
          : undefined,
      value: taxesTier,
      kind: 'taxes-tier',
    });
  }
  if (spendingTotal > 0) {
    allocs.push({
      key: null,
      label: 'Spending',
      value: spendingTotal,
      kind: 'spending-tier',
    });
  }
  if (saving > 0) {
    allocs.push({
      key: null,
      label: 'Saving',
      sublabel:
        retirementNode != null
          ? 'incl. retirement contributions'
          : 'not spent this window',
      value: saving,
      kind: 'saving-tier',
    });
  }
  // Last in the column: never inside Spending, never fanned into the
  // category columns, and below Saving so its label sits in open space
  // rather than under the Spending fan (it is usually a sliver, whose
  // one-line label reaches toward the group bars).
  if (unreviewedOut > 0) {
    allocs.push(unreviewedNode(unreviewedOut));
  }

  return applyTieredScaleBreak({
    sources,
    allocs,
    groupCol,
    cats,
    totals: {
      income: incomeTotal,
      taxes: taxesTier,
      spending: spendingTotal,
      saving,
      fromSavings,
      unreviewed: unreviewedNet,
      unreviewedCount: unreviewed?.count ?? 0,
    },
    scaleBreak: null,
  });
};

type BuildPayeeFlowArgs = {
  groups: FlowGroup[];
  /** Most rows to show per side before folding the tail. */
  maxNodes?: number;
};

/**
 * The drill for one category: payees with net inflows join the left (this is
 * the offset rule made visible — the people and refunds that reduced the
 * category's net), payees with net outflows sit on the right, and a
 * take-home band absorbs the difference so the sides balance. Works for
 * income categories too, where the band lands on the right instead.
 */
export const buildPayeeFlow = ({
  groups,
  maxNodes = 7,
}: BuildPayeeFlowArgs): FlowModel => {
  const inflows = groups.filter((group) => group.net > 0);
  const outflows = groups.filter((group) => group.net < 0);

  const inflowTotal = inflows.reduce((sum, group) => sum + group.net, 0);
  const outflowTotal = outflows.reduce((sum, group) => sum - group.net, 0);
  const fromTakeHome = outflowTotal - inflowTotal;

  const sources: FlowNode[] = [];
  if (fromTakeHome > 0) {
    sources.push({
      key: null,
      label: 'From take-home',
      value: fromTakeHome,
      kind: 'take-home',
    });
  }
  sources.push(
    ...foldTail(
      inflows
        .map(
          (group): FlowNode => ({
            key: group.key,
            label: group.key ?? 'No payee',
            value: group.net,
            kind: 'income',
          }),
        )
        .sort(byValueDesc),
      maxNodes,
      'Other inflows',
    ),
  );

  const dests: FlowNode[] = foldTail(
    outflows
      .map(
        (group): FlowNode => ({
          key: group.key,
          label: group.key ?? 'No payee',
          value: -group.net,
          kind: 'spend',
        }),
      )
      .sort(byValueDesc),
    maxNodes,
    'Everything else',
  );
  if (fromTakeHome < 0) {
    dests.push({
      key: null,
      label: 'To take-home',
      value: -fromTakeHome,
      kind: 'take-home',
    });
  }

  return {
    sources,
    dests,
    totals: {
      spend: outflowTotal,
      income: inflowTotal,
      covered: Math.min(outflowTotal, inflowTotal),
      fromSavings: Math.max(0, fromTakeHome),
      saved: Math.max(0, -fromTakeHome),
    },
    scaleBreak: null,
  };
};
